// src/components/SiteSlideshow.js
//
// The background behind the home page heading, as a slideshow of the photos
// set in the dashboard (or the built-in farm photo alone, which simply stays).
//
// The photos change as useSlides describes: the next is ready before it is
// shown, and fades in over the last. Nothing runs while the page is in a
// background tab or the hero has been scrolled away, and a visitor who has
// asked their device for less motion starts paused.
// A small pill in the corner shows where the slideshow is and pauses it, so
// moving content can always be stopped.
import React, { useRef, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ProductPhoto from './ProductPhoto';
import { BACKDROP_COLOR } from './SiteBackdrop';
import useSiteData from './useSiteData';
import { prefersReducedMotion, useOutOfSight, useSlides } from './useSlides';
import { sitePhotos, slideSeconds } from '../photos';

const FADE_MS = 1200;

/**
 * Sits behind everything else in its parent, which must be positioned and
 * isolated (`position: relative; isolation: isolate`), like SiteBackdrop.
 */
const SiteSlideshow = ({ slot = 'hero', sizes = '100vw' }) => {
  const data = useSiteData();
  const photos = data ? sitePhotos(slot, data) : [];
  const count = photos.length;
  const frameRef = useRef(null);
  const [paused, setPaused] = useState(prefersReducedMotion);
  const outOfSight = useOutOfSight(frameRef);

  const { index, mounted, go, markReady, layer } = useSlides({
    keys: photos.map((photo) => photo.key),
    playing: !paused && !outOfSight,
    seconds: slideSeconds(data),
  });

  return (
    <>
      <Box
        ref={frameRef}
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: -1,
          overflow: 'hidden',
          backgroundColor: BACKDROP_COLOR,
        }}
      >
        {photos.map((photo, i) =>
          mounted.has(i) ? (
            <Box key={photo.key} sx={{ position: 'absolute', inset: 0, ...layer(i, FADE_MS) }}>
              <ProductPhoto
                photo={photo}
                sizes={sizes}
                // The first is the page's largest image and is wanted now;
                // the others are only in the page because they are next.
                eager
                priority={i === 0 ? 'high' : 'low'}
                onReady={() => markReady(i)}
                sx={{ position: 'absolute', inset: 0, backgroundColor: 'transparent' }}
              />
            </Box>
          ) : null
        )}
      </Box>

      {count > 1 && (
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 12, md: 20 },
            right: { xs: 12, md: 20 },
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            pl: 1.25,
            pr: 0.25,
            py: 0.25,
            borderRadius: 999,
            backgroundColor: 'rgba(20, 22, 15, 0.45)',
            backdropFilter: 'blur(4px)',
          }}
        >
          {photos.map((photo, i) => (
            <Box
              key={photo.key}
              component="button"
              type="button"
              onClick={() => go(i)}
              aria-label={`Show background photo ${i + 1}`}
              aria-current={i === index ? 'true' : undefined}
              sx={{
                p: 0,
                border: 0,
                width: i === index ? 16 : 6,
                height: 6,
                borderRadius: 999,
                cursor: 'pointer',
                transition: 'width .3s ease, background-color .3s ease',
                backgroundColor: i === index ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.5)',
              }}
            />
          ))}
          <IconButton
            size="small"
            onClick={() => setPaused((value) => !value)}
            aria-label={paused ? 'Play the background slideshow' : 'Pause the background slideshow'}
            sx={{ color: '#fff', p: 0.5 }}
          >
            {paused ? (
              <PlayArrowRoundedIcon sx={{ fontSize: 18 }} />
            ) : (
              <PauseRoundedIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Box>
      )}
    </>
  );
};

export default SiteSlideshow;
